/** Colour maths for team accents on a near-black UI (WCAG relative luminance). */
import { team } from '@clutch/core';

export const INK = '#0B0B0D';

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Mix `hex` toward white by `t` (0–1). */
export function lighten(hex: string, t: number): string {
  const [r, g, b] = rgb(hex).map((v) => Math.round(v + (255 - v) * t)) as [number, number, number];
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * A team colour that reads on the dark background (≥ 3:1, used for large text,
 * bars and rings). Tries primary, then secondary, then lightens primary.
 */
export function teamAccent(tricode: string, bg = INK): string {
  const t = team(tricode);
  if (contrast(t.primary, bg) >= 3) return t.primary;
  if (contrast(t.secondary, bg) >= 3 && luminance(t.secondary) < 0.85) return t.secondary;
  for (let k = 0.1; k <= 1; k += 0.1) {
    const c = lighten(t.primary, k);
    if (contrast(c, bg) >= 3) return c;
  }
  return '#FFFFFF';
}

/** Black or white text, whichever contrasts more with `bg`. */
export function readableOn(bg: string): '#000000' | '#FFFFFF' {
  return contrast('#000000', bg) >= contrast('#FFFFFF', bg) ? '#000000' : '#FFFFFF';
}
